import { DEFAULT_BUCKET } from '@integrate/contracts';
import { hasOwn } from './domain.js';
export function currentBucket(design, personId) {
    return hasOwn(design.buckets, personId) ? design.buckets[personId] : DEFAULT_BUCKET;
}
/** Stage attribution for a carve-out member, or null if they are not one. */
export function bucketMetaFor(design, personId) {
    if (currentBucket(design, personId) !== 'Carve-out')
        return null;
    return hasOwn(design.bucketMeta, personId)
        ? design.bucketMeta[personId]
        : { src: 'manual', joinStage: 'perimeter' };
}
/** Build-ups created before stages existed are Day 1. */
export function waveOf(p) {
    return p.wave === 'day2' ? 'day2' : 'day1';
}
/** Does this person leave the carve-out again at Day 2 rightsizing? */
export function isDay2Exiter(design, p) {
    if (currentBucket(design, p.id) !== 'Carve-out')
        return false;
    if (p.isBuildUp)
        return !!p.day2Exit;
    return !!(design.bucketMeta[p.id] ?? {}).day2Exit;
}
/**
 * Is this person inside the carve-out org at the given stage?
 *
 * Build-ups follow their own rule rather than bucketMeta: they do not exist yet in the perimeter
 * (nobody has been hired), a Day 1 hire is present at Day 1 and stays at Day 2 unless cut, and a
 * Day 2 hire appears only at Day 2. Missing this branch silently drops every planned hire out of the
 * stage lens.
 */
export function stageIncludesId(design, personId, stage) {
    const buildUp = design.buildUps.find((b) => String(b.id) === String(personId));
    if (buildUp) {
        const wave = waveOf(buildUp);
        if (stage === 'perimeter')
            return false;
        if (stage === 'day1')
            return wave === 'day1';
        return wave === 'day1' ? !buildUp.day2Exit : true;
    }
    const meta = bucketMetaFor(design, personId);
    if (!meta)
        return false; // not a carve member at all
    if (stage === 'perimeter')
        return meta.joinStage === 'perimeter';
    if (stage === 'day1')
        return true; // perimeter members plus transfers
    return !meta.day2Exit; // day2: everyone still aboard after rightsizing
}
/** The stage lens only refines the carve-out perspective; every other perspective ignores it. */
export function stagePasses(design, personId, perspective, stage) {
    if (perspective !== 'carve-out')
        return true;
    return stageIncludesId(design, personId, stage);
}
/**
 * Who can carry a stranded-plan mark: a real, current, Remain-side person with dedication > 0.
 *
 * Note that dedication must be numeric. A legacy boolean split-head marker leaves dedication null,
 * so such a person is deliberately NOT a carrier — they are known to be shared but by an unknown
 * amount, and inventing one would put a fabricated number into the stranded cost.
 */
export function strandedEligible(design, p) {
    if (p.isBuildUp || p.isLeaver)
        return false;
    const bucket = currentBucket(design, p.id);
    if (bucket === 'Carve-out' || bucket === 'No longer required')
        return false;
    return p.dedication != null && p.dedication > 0;
}
/** The person's validated plan entry, or null (no plan, not eligible, or a garbled entry). */
export function strandedPlanOf(design, p) {
    if (!strandedEligible(design, p))
        return null;
    const entry = design.strandedPlan[p.id];
    if (!entry || (entry.action !== 'rightsize' && entry.action !== 'redeploy'))
        return null;
    return { action: entry.action, month: clampMonth(entry.month) };
}
export function clampMonth(value) {
    return Math.max(0, Math.min(36, Math.round(Number(value) || 0)));
}
/**
 * Everyone a stranded plan may be recorded against, in roster order.
 *
 * NOT the same population as the app's `strandedCarriers` (orgchart.html:6409), and deliberately named
 * differently to stop the two being confused. That one additionally requires `countsInHeadcount`, honours
 * the chart's filters and sorts by stranded cost — it is the list a consultant works through, and its
 * total is `computeStrandedTimeline().grossRunRate`.
 *
 * This one is the *eligibility* set: an open position or an inactive person with dedication can carry a
 * plan, which imported files do, and something has to be able to find them in order to offer a way back.
 * Summing anything over this set will not match the timeline, and that is not a bug in either.
 */
export function strandedPlanCandidates(state) {
    return state.employees.filter((p) => strandedEligible(state.design, p));
}
/**
 * Derived shared-person marker: partially dedicated, or a legacy boolean marker.
 * Fully dedicated (1) and fully undedicated (0) are not shared.
 */
export function isSharedPerson(dedication, legacyMarker) {
    return legacyMarker === true || (dedication != null && dedication > 0 && dedication < 1);
}
//# sourceMappingURL=decisions.js.map